import { AttributeDefinition } from 'domain/experiment/attribute';

import { isLocalStorageCompatibleShape } from './local-storage-compatible';

export type CompoundSearchCriterionType = 'regex' | 'operator' | 'lparen' | 'rparen';

export type CompoundSearchCriterion = {
  type: CompoundSearchCriterionType;
  value: string;
};

type ColumnType =
  | 'bool'
  | 'complex'
  | 'datetime'
  | 'evaluationSeries'
  | 'experimentState'
  | 'file'
  | 'fileSeries'
  | 'fileSet'
  | 'float'
  | 'floatSeries'
  | 'gitRef'
  | 'histogramSeries'
  | 'imageSeries'
  | 'int'
  | 'string'
  | 'stringSeries'
  | 'stringSet'
  | 'notSupported';
type BaseColumn = {
  id: string;
  columnType: ColumnType;
  aggregationMode?: 'last' | 'min' | 'max' | 'average' | 'variance' | 'auto';
  attributeName?: string;
  pinned: boolean;
  width: number;
  displayName?: string;
  color?: string;
  exportable: boolean;
  groupable: boolean;
  hideable: boolean;
  reorderable:
    | boolean
    | {
        draggable?: boolean;
        droppable?:
          | {
              before?: boolean;
              after?: boolean;
            }
          | boolean;
      };
  resizable: boolean;
  sortable: boolean;
  autoWidthEnabled?: boolean;
  renamable?: boolean;
  recolorable?: boolean;
};

export type Column =
  | BaseColumn
  | (BaseColumn & {
      columnType: 'float';
      displayMode: 'scientific' | 'decimal' | 'auto';
    })
  | (BaseColumn & {
      columnType: 'floatSeries';
      aggregationMode: 'last' | 'min' | 'max' | 'average' | 'variance' | 'auto';
      displayMode: 'scientific' | 'decimal' | 'auto';
    });

export interface SearchQuery {
  criteria: Array<SearchQuery | SearchCriterion>;
  operator: 'and' | 'or';
}

export type SearchCriterion = SearchCriterionWithValue | SearchCriterionWithoutValue;

type SearchCriterionWithValue =
  | BooleanCriterion
  | DateTimeCriterion
  | GitRefCriterion
  | IntegerCriterion
  | FloatCriterion
  | FloatSeriesCriterion
  | SingularExperimentStateCriterion
  | StringCriterion
  | StringSeriesCriterion
  | SearchCriterionWithArrayValue;

type SearchCriterionWithArrayValue =
  | OwnerCriterion
  | NonEmptyStringSetCriterion
  | PluralExperimentStateCriterion;

type SearchCriterionWithoutValue = EmptyCriterion | EmptyStringSetCriterion;

type BaseSearchCriterion = {
  // attribute name
  attribute: string;
  subproperty?: string;
};

type NonEmptyStringSetCriterion = BaseSearchCriterion & {
  type: 'stringSet';
  operator: 'oneOf' | '!oneOf' | 'allOf';
  value: string[];
};

type EmptyStringSetCriterion = BaseSearchCriterion & {
  type: 'stringSet';
  operator: 'empty' | '!exists';
};

type BooleanCriterion = BaseSearchCriterion & {
  type: 'bool';
  operator: '=' | '!=';
  value: boolean;
};

type GitRefCriterion = BaseSearchCriterion & {
  type: 'gitRef';
  operator: '=';
  value: string;
};

type IntegerCriterion = BaseSearchCriterion & {
  type: 'int';
  operator: '=' | '!=' | '>=' | '>' | '<=' | '<';
  value: number;
};

type StringCriterion = BaseSearchCriterion & {
  type: 'string';
  operator: '=' | '!=' | 'contains' | '!contains' | 'matches' | '!matches';
  value: string;
};

type StringSeriesCriterion = BaseSearchCriterion & {
  type: 'stringSeries';
  operator: '=' | '!=' | 'contains' | '!contains' | 'matches' | '!matches';
  value: string;
  subproperty: string;
};

type FloatCriterion = BaseSearchCriterion & {
  type: 'float';
  operator: '=' | '!=' | '>=' | '>' | '<=' | '<';
  value: number;
};

type FloatSeriesCriterion = BaseSearchCriterion & {
  type: 'floatSeries';
  operator: '=' | '!=' | '>=' | '>' | '<=' | '<' | '!exists';
  value: number;
  subproperty: string;
};

type DateTimeCriterion = BaseSearchCriterion & {
  type: 'datetime';
  // was: operator: 'after' | 'before'
  operator: 'after' | 'before' | 'last';
  value: string;
};

type SingularExperimentStateCriterion = BaseSearchCriterion & {
  type: 'experimentState';
  operator: '=';
  value: 'idle' | 'running';
};

type PluralExperimentStateCriterion = BaseSearchCriterion & {
  type: 'experimentState';
  operator: 'oneOf';
  value: Array<'idle' | 'running'>;
};

type OwnerCriterion = BaseSearchCriterion & {
  type: 'owner';
  operator: 'oneOf' | '!oneOf';
  value: string[];
};

type EmptyCriterion = BaseSearchCriterion & {
  type: 'int' | 'float' | 'string' | 'datetime' | 'floatSeries';
  operator: 'exists' | '!exists';
};

export type JsonCompatibleAxisRange = [number | undefined | null, number | undefined | null];

export type AxisScale = 'linear' | 'logarithmic' | 'symlog';

export type ChartGlobalConfigLocalOptions = {
  xAxisMetric?: AttributeDefinition;
  xAxisMode?: 'step' | 'relativeTime' | 'absoluteTime' | 'timeSeries' | 'customExpressionSeries';
  xAxisScale?: AxisScale;
  yAxisScale?: AxisScale;
  showMetricBy?: 'lineStyle' | 'color';
  smoothing?: number;
  smoothingAlgorithm?: 'emaCausal' | 'gaussianCentered' | 'smaCausal' | 'smaCentered';
  showOriginalLineOnSmoothing?: boolean;
  showCompletion?: boolean;
  xAxisRange?: JsonCompatibleAxisRange;
  normalizeToFirstValue?: boolean;
  outliersDisplayMode?: 'none' | 'outline';
  metricsStepsRange?: JsonCompatibleAxisRange;
};

export type ChartWidgetLocalOptions = {
  xAxisScale?: AxisScale;
  yAxisScale?: AxisScale;
  xAxisMode?: 'step' | 'relativeTime' | 'absoluteTime' | 'timeSeries' | 'customExpressionSeries';
  xAxisMetric?: AttributeDefinition;
  yAxisRange?: JsonCompatibleAxisRange;
  xAxisRange?: JsonCompatibleAxisRange;
  metricsStepsRange?: JsonCompatibleAxisRange;
  showCompletion?: boolean;
  normalizeToFirstValue?: boolean;
  smoothing?: number;
  smoothingAlgorithm?: 'emaCausal' | 'gaussianCentered' | 'smaCausal' | 'smaCentered';
  showOriginalLineOnSmoothing?: boolean;
  outliersDisplayMode?: 'none' | 'outline';
};

export type LocalStorageData = {
  compareTabByProject?: Record<string, string | undefined>;
  lastDashboardIds?: { [projectPath: string]: string };
  chartWidgetOptionsByProject?: Record<string, Record<string, ChartWidgetLocalOptions>>;
  defaultOrganizationName?: string;
  dontShowInviteUsersBanner?: boolean;
  dontShowArchiveExperimentHeadConfirmation?: boolean;
  dontShowBrowserSupportInfo?: boolean;
  expandableLayout?: Record<string, Record<string, string | undefined> | undefined>;
  lastExperimentDashboardTab?: string;
  orderedColumns?: Record<string, Record<string, Column[]>>;
  os?: 'windows' | 'mac' | 'linux';
  stringSeriesTimeScale?: Record<string, 'none' | 'relativeTime' | 'absoluteTime' | undefined>;
  shownHelp?: boolean;
  leaderboardPageSize?: Record<string, number | undefined>;
  bulkAcceptInvitationMuted?: {
    byId: Record<string, number | undefined>;
    projects: Record<string, number | undefined>;
    organizations: Record<string, number | undefined>;
  };
  lbViewIdByProject?: Record<string, string | undefined>;
  runColorListNameByProject?: Record<string, string | undefined>;
  recentMemos?: Record<string, Record<string, string[] | undefined> | undefined>;
  recentSearchQueries?: Record<string, Record<string, SearchQuery[] | undefined> | undefined>;
  recentCompoundQueries?: Record<
    string,
    Record<string, CompoundSearchCriterion[][] | undefined> | undefined
  >;
  textWrapping?: Record<string, Record<string, string | undefined> | undefined>;
  projectLeaderboardSplitPx?: number;
  reportsTableSplitPx?: number;
  attributeViewerSplitRate?: Record<string, number | undefined> | undefined;
  lastProjectTab?: string;
  lastProjectRunsTab?: 'table' | 'details' | 'compare';
  structureAsideCollapsed?: boolean;
  chartGlobalConfig?: Record<string, ChartGlobalConfigLocalOptions>;
  sidebarCollapsed?: boolean;
  dismissedInfoBanners?: Record<string, boolean>;
  dismissedInfoModals?: Record<string, boolean>;
  colors?: Record<string, Record<string, string>>;
  presetColors?: Record<string, string[]>;
  pinWidgetLegend?: boolean;
  errorBandsHidden?: boolean;
  referenceLinesHidden?: boolean;
  myRunsTab?: {
    experimentsOnly?: boolean;
    sortBy?: string;
    sortFieldType?: string;
    sortFieldAggregationMode?: string;
    sortDirection?: string;
  };
  attributeViewerSettingsByProject?: Record<string, { outliersDisplayMode?: 'none' | 'outline' }>;
  reportDismissedNewerVersionToasts?: Record<string, number>;
  reportTableHeight?: 'small' | 'medium' | 'large';
  reportOutlineWidthPx?: number;
  reportOutlineOpen?: boolean;
  dashboardOutlineWidthPx?: number;
  dashboardOutlineOpen?: boolean;
  reportSectionFolding?: Record<string, Record<string, boolean>>;
};
export const version = 18 as const;
export type LocalStorageV18 = {
  version: typeof version;
  data: LocalStorageData;
};

export function isLocalStorageV18(input: any): input is LocalStorageV18 {
  return isLocalStorageCompatibleShape(input) && input.version === version;
}
